import {useMutation, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {AnalyticsEvent} from "../utils/analytics";
import {useClientV2} from "../utils/hooks";
import {LEGACY_PROFILE_QUERY_KEY} from "../features/profile/hooks/useProfile";

const useRequestEmailTwoFactorSetup = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: () =>
      client(`settings/two-factor/request?method=email`, {
        method: "GET",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to send OTP");
      AnalyticsEvent("Two-factor setup", "Email OTP send fail");
    },
    onSuccess: () => {
      message.success("An OTP has been sent to your email address");
      AnalyticsEvent("Two-factor setup", "Email OTP send success");
    },
  });
};

const useActivateEmailTwoFactor = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {twofaCode: string}) =>
      client(`settings/two-factor/email/activate`, {
        method: "PATCH",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(
        error.message || "Failed to activate email two-factor authentication"
      );
      AnalyticsEvent("Two-factor setup", "Email activation fail");
    },
    onSuccess: () => {
      message.success("Email two-factor authentication enabled successfully");
      AnalyticsEvent("Two-factor setup", "Email activation success");
      queryClient.invalidateQueries({queryKey: [LEGACY_PROFILE_QUERY_KEY]});
    },
  });
};

const useInitiateTotp = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: () =>
      client(`settings/two-factor/totp/initiate`, {
        method: "POST",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to start authenticator app setup");
      AnalyticsEvent("Two-factor setup", "TOTP initiate fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Two-factor setup", "TOTP initiate success");
    },
  });
};

const useActivateTotp = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {twofaCode: string}) =>
      client(`settings/two-factor/totp/activate`, {
        method: "POST",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to activate authenticator app");
      AnalyticsEvent("Two-factor setup", "TOTP activation fail");
    },
    onSuccess: () => {
      message.success("Authenticator app two-factor authentication enabled successfully");
      AnalyticsEvent("Two-factor setup", "TOTP activation success");
    },
  });
};

const useDisableTwoFactorRequest = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: () =>
      client(`settings/two-factor/disable/request`, {
        method: "GET",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to send OTP");
      AnalyticsEvent("Two-factor disable", "OTP send fail");
    },
    onSuccess: () => {
      message.success("An OTP has been sent to your email address");
      AnalyticsEvent("Two-factor disable", "OTP send success");
    },
  });
};

const useDisableEmailTwoFactor = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {twofaCode: string}) =>
      client(`settings/two-factor/email/disable`, {
        method: "PATCH",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to disable two-factor authentication");
      AnalyticsEvent("Two-factor disable", "Email disable fail");
    },
    onSuccess: () => {
      message.success("Two-factor authentication disabled successfully");
      AnalyticsEvent("Two-factor disable", "Email disable success");
      queryClient.invalidateQueries({queryKey: [LEGACY_PROFILE_QUERY_KEY]});
    },
  });
};

const useDisableTotpTwoFactor = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {twofaCode: string}) =>
      client(`settings/two-factor/totp/disable`, {
        method: "PATCH",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to disable two-factor authentication");
      AnalyticsEvent("Two-factor disable", "TOTP disable fail");
    },
    onSuccess: () => {
      message.success("Two-factor authentication disabled successfully");
      AnalyticsEvent("Two-factor disable", "TOTP disable success");
      queryClient.invalidateQueries({queryKey: [LEGACY_PROFILE_QUERY_KEY]});
    },
  });
};

const useRegenerateBackupCodes = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {twofaCode: string}) =>
      client(`settings/two-factor/totp/regenerate-backup-codes`, {
        method: "POST",
        data: payload,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to regenerate recovery codes");
      AnalyticsEvent("Two-factor backup codes", "Regenerate fail");
    },
    onSuccess: () => {
      message.success("Recovery codes regenerated successfully");
      AnalyticsEvent("Two-factor backup codes", "Regenerate success");
    },
  });
};

const useRequestWithdrawal2FA = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: () =>
      client(`payments/withdrawal/2fa/request`, {
        method: "GET",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message || "Failed to send OTP");
      AnalyticsEvent("Withdrawal two-factor", "Email OTP send fail");
    },
    onSuccess: () => {
      message.success("An OTP has been sent to your email address");
      AnalyticsEvent("Withdrawal two-factor", "Email OTP send success");
    },
  });
};

export {
  useRequestEmailTwoFactorSetup,
  useActivateEmailTwoFactor,
  useInitiateTotp,
  useActivateTotp,
  useDisableTwoFactorRequest,
  useDisableEmailTwoFactor,
  useDisableTotpTwoFactor,
  useRegenerateBackupCodes,
  useRequestWithdrawal2FA,
};
